import React, { PureComponent } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';

import { Row, Col } from 'react-bootstrap';
import { Select, Button, RoundButton, IconButton, CreatableSelect, Loader } from 'sa-ui';

import XLSX from 'sheetjs-style';

import SimulatedAbilityModal from './SimulatedAbilityModal';
import SimulatedRemoveModal from '../remove-modal/SimulatedRemoveModal';
import SimulatedRemoveSuccessfullyModal from '../remove-modal/SimulatedRemoveSuccessfullyModal';
import SimulatedRemoveProblemModal from '../remove-modal/SimulatedRemoveProblemModal';
import SimulatedApproveModal from '../approve-modal/SimulatedApproveModal';
import SimulatedAbilityConfirmationModal from './SimulatedAbilityConfirmationModal';
import SimulatedContentMapAbilities from './SimulatedContentMapAbilities';

import { promisify } from 'app/actions';
import * as actions from './simulatedContentMapActions';
import { removeContentmapAbilities } from '../simulatedActions';

import messages from './simulatedContentMapMessages';

import * as helpers from './simulatedContentMapHelpers';
import AbilityModalType from './simulatedAbilityModalType';

const SimulatedContentMapContainer = (props) => {
  const [errorFeedback, setErrorFeedback] = React.useState('');
  const [modals, setModals] = React.useState({});
  const [filter, setFilter] = React.useState({});
  const [abilityModalType, setAbilityModalType] = React.useState(AbilityModalType.ADD);
  const [ability, setAbility] = React.useState();
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(async () => {
    setIsLoading(true);
    await props.checkCoordinatorPermission({ idEvaluation: props.idEvaluation });
    await props.getEvaluationInformation({ idEvaluation: props.idEvaluation });
    await props.getCurricularOptions({
      idEvaluation: props.idEvaluation,
      catalogingType: 'discipline',
    });
    await props.getAbilities({ idEvaluation: props.idEvaluation });
    setIsLoading(false);
  }, [props.idEvaluation]);

  React.useEffect(() => {
    props.applyFilter(filter);
  }, [filter]);

  async function update() {
    await props.getEvaluationInformation({ idEvaluation: props.idEvaluation });
    await props.getAbilities({ idEvaluation: props.idEvaluation });
  }

  async function handleChangeCurricular(event) {
    await props.getUnitsOptions({
      idEvaluation: props.idEvaluation,
      catalogingType: 'matrix',
      idCatalogingDiscipline: event.target.value,
      idAlternativeMatrix:
        props.evaluation.evaluation.idAlternativeMatrix > 0
          ? props.evaluation.evaluation.idAlternativeMatrix
          : null,
      view: '1',
    });
    setFilter({ ...filter, curricular: event.target.value, unit: undefined });
  }

  function handleChangeUnit(event) {
    setFilter({ ...filter, unit: event.target.value });
  }

  function handleChangeStatus(event) {
    setFilter({ ...filter, status: event.target.value });
  }

  function clearFilters() {
    setFilter({});
    props.clearFilter();
  }

  function addAbility() {
    setAbilityModalType(AbilityModalType.ADD);
    setAbility(undefined);
    setModals({ ability: true });
  }

  function handleCloseAbilityModal() {
    setModals({ ability: false });
    setAbility(undefined);
  }

  async function handleAdd(data) {
    await props.addContentMapAbility(data);
    update();
    setModals({ ability: false, abilityConfirmation: true });
  }

  async function handleEdit(data) {
    await props.editContentMapAbility(data);
    update();
    setModals({ ability: false, abilityConfirmation: true });
    setAbility(undefined);
  }

  async function handleRequestRemoveAbility(selectedAbility) {
    setModals({ remove: true });
    setAbility(selectedAbility);
  }

  async function handleApprove(data) {
    await props.approveContentMapAbility(data);
    update();
    setModals({ ability: false, abilityConfirmation: true });
    setAbility(undefined);
  }

  async function handleReprove(data) {
    await props.reproveContentMapAbility(data);
    update();
    setModals({ ability: false, abilityConfirmation: true });
    setAbility(undefined);
  }

  const handleEditAbility = (abilityData) => {
    setAbilityModalType(AbilityModalType.EDIT);
    setAbility(abilityData);
    setModals({ ability: true });
  };

  const handleApproveAbility = (abilityData) => {
    setAbilityModalType(AbilityModalType.APPROVE);
    setAbility(abilityData);
    setModals({ ability: true });
  };

  const handleReproveAbility = (abilityData) => {
    setAbilityModalType(AbilityModalType.REPROVE);
    setAbility(abilityData);
    setModals({ ability: true });
  };

  const handleVisualizeAbility = (abilityData) => {
    setAbilityModalType(AbilityModalType.VIEW);
    setAbility(abilityData);
    setModals({ ability: true });
  };

  const handleApproveAllAbilities = async () => {
    await props.approveAllAbilities({ idEvaluation: props.idEvaluation });
    props.getAbilities({ idEvaluation: props.idEvaluation });
  };

  const handleCloseApproveAllAbilites = () => {
    props.resetApproveAllAbilitiesStatus();
    setModals({ approve: false });
  };

  const removeAbility = async () => {
    if (!ability) return;
    try {
      await props.removeContentmapAbilities({
        idAbility: ability.abilityId,
        idEvaluation: props.idEvaluation,
      });
      update();
      setModals({ removeSuccessfully: true });
      setAbility();
    } catch (err) {
      setModals({ removeProblem: true });
    }
  };

  function isContentMapCompleted() {
    if (!props.evaluation || !props.evaluation.evaluation || !props.evaluation.evaluation.dates)
      return false;
    return props.evaluation.evaluation.dates.some(
      (d) => d.component === 'contentmap' && d.complete === true,
    );
  }

  function downloadContentMapSpreadsheet(data) {
    const alphabet = ['A', 'B', 'C', 'D', 'E'];

    // Table header
    const tableHeader = [
      props.intl.formatMessage(messages.id),
      props.intl.formatMessage(messages.curricularComponent),
      props.intl.formatMessage(messages.objectOfKnowledge),
      props.intl.formatMessage(messages.officialAbility),
      props.intl.formatMessage(messages.indicator),
    ];

    // Table content
    let tableContent = [];

    data.map((item, index) => {
      let curricularComponent =
        (item.catalogingSegment && item.catalogingSegment.name
          ? item.catalogingSegment.name + ' | '
          : '') +
        (item.catalogingLevel && item.catalogingLevel.name
          ? item.catalogingLevel.name + ' | '
          : '') +
        (item.catalogingDiscipline.length > 0 ? item.catalogingDiscipline[0].cc_name : '');

      tableContent.push([
        item.abilityId || '',
        curricularComponent || '',
        item.matrixContent || item.content,
        item.oficialCatalogingName || '',
        item.schoolAbilityName || '',
      ]);
    });

    const tableData = [tableHeader].concat(tableContent);

    const ws = XLSX.utils.aoa_to_sheet(tableData);

    let columnsWidth = [{ width: 10 }, { width: 25 }, { width: 30 }, { width: 40 }, { width: 25 }];

    // Header styles
    tableHeader.forEach((item, index) => {
      ws[alphabet[index] + '1'].s = {
        font: { bold: true },
        alignment: { horizontal: 'center' },
        border: {
          top: { style: 'thin', color: '00735e' },
          bottom: { style: 'thin', color: '00735e' },
          left: { style: 'thin', color: '00735e' },
          right: { style: 'thin', color: '00735e' },
        },
      };

      // if (item.length > columnsWidth[index].width) {
      //   columnsWidth[index].width = item.length;
      // }
    });

    // Content styles
    tableContent.forEach((row, rIdx) => {
      row.forEach((col, cIdx) => {
        let cellStyle = {
          border: {
            top: { style: 'thin', color: '00735e' },
            bottom: { style: 'thin', color: '00735e' },
            left: { style: 'thin', color: '00735e' },
            right: { style: 'thin', color: '00735e' },
          },
          alignment: { wrapText: true, vertical: 'center', horizontal: 'center' },
        };

        // Adjust column width
        // if (col.length > columnsWidth[cIdx].width) {
        //   columnsWidth[cIdx].width =  alphabet[cIdx] === 'E' ? col.length + 5 : col.length;
        // }

        ws[alphabet[cIdx] + (rIdx + 2)].s = cellStyle;
      });
    });

    ws['!cols'] = columnsWidth;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, props.intl.formatMessage(messages.abilities));

    // Generate XLSX file and send to client
    const filename =
      props.intl.formatMessage(messages.sheetFilename, {
        idEvaluation: props.idEvaluation,
      }) + '.xlsx';

    XLSX.writeFile(wb, filename);
  }

  const contentMapCompleted = isContentMapCompleted();
  const isProfessor = props.isCoordinator.permission === 0 && props.isCoordinator.edit === 1;

  if (!props.evaluation || !props.evaluation.evaluation) return null;

  // console.log(props);

  return (
    <React.Fragment>
      <Loader show={props.loading || isLoading} />
      <a
        className="return"
        onClick={props.history.goBack}
        style={{ color: '#f59f8d', cursor: 'pointer' }}
      >
        <i className="ti-arrow-left"> </i> <FormattedMessage {...messages.back} />
      </a>
      {/* <FormattedMessage {...messages.title}>
        {(txt) => <h5 className="title">{txt}</h5>}
      </FormattedMessage> */}
      <h6 className="subtitle" style={{ marginTop: 15 }}>
        {props.evaluation.evaluation.levelSegment.map((l) => (
          <div>{l}</div>
        ))}
        {props.contract && <div>{props.contract.application}</div>}
      </h6>
      <div style={{ marginBottom: 25 }}>
        <h6 className="id mb-0">
          <div>
            {props.evaluation.evaluation.catalogings.map((c) => c.abbreviation).join(' | ')}
          </div>
          <div>ID: {props.evaluation.evaluation.idString}</div>
        </h6>
      </div>

      {/* <FormattedMessage {...messages.addedAbilitys}>
        {(txt) => <h5 className="title">{txt}</h5>}
      </FormattedMessage>{' '} */}

      <div style={{ clear: 'both' }}>
        <h6 className="filter subtitle" style={{ float: 'left' }}>
          <i className="pe-7s-filter" />
          <FormattedMessage {...messages.filters} />
          <FormattedMessage {...messages.clear}>
            {(txt) => (
              <button className="btn btn-primary btn-sm ml-3" onClick={clearFilters}>
                {txt}
              </button>
            )}
          </FormattedMessage>
        </h6>
        {props.abilities.length > 0 &&
          props.isCoordinator &&
          props.isCoordinator.permission !== 0 && (
            <h6 className="filter subtitle" style={{ float: 'right' }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => downloadContentMapSpreadsheet(props.abilitiesFiltered)}
              >
                <FormattedMessage {...messages.generateSheet} />
              </button>
            </h6>
          )}
      </div>
      <Row class="mt-3" style={{ clear: 'both' }}>
        <Col xs={12} md={4} className="mb-4">
          <Select
            placeholder={props.intl.formatMessage(messages.curricularComponent)}
            nullable
            options={props.curricularOptions}
            onChange={handleChangeCurricular}
            value={filter.curricular}
          />
        </Col>
        <Col xs={12} md={4} className="mb-4">
          <Select
            placeholder={props.intl.formatMessage(messages.unit)}
            nullable
            disabled={!filter.curricular}
            onChange={handleChangeUnit}
            options={props.unitsOptions}
            value={filter.unit}
          />
        </Col>
        <Col xs={12} md={4} className="mb-4">
          <Select
            placeholder={props.intl.formatMessage(messages.status)}
            onChange={handleChangeStatus}
            nullable
            options={helpers.getStatusOptions(props.abilities, props.intl)}
            value={filter.status}
          />
        </Col>
      </Row>
      <Row>
        <div className="align-middle" style={{ width: '100%' }}>
          <div className="row align-items-center mb-4">
            <Col lg={3}>
              <h6 className="habilidades">
                <i className="pe-7s-sun" />
                <FormattedMessage {...messages.abilities} /> ({props.abilitiesFiltered.length})
              </h6>
            </Col>
            <Col lg={6} />
            <Col lg={2}>
              {[1, 2].includes(props.isCoordinator.permission) &&
                !props.stepConcluded &&
                !isLoading && (
                  <FormattedMessage {...messages.approveAll}>
                    {(txt) => (
                      <IconButton
                        label={txt}
                        size={30}
                        onClick={() => setModals({ approve: true })}
                      >
                        <i className="icon ti-check-box" />
                      </IconButton>
                    )}
                  </FormattedMessage>
                )}
            </Col>
            <Col lg={1}>
              {isProfessor && !contentMapCompleted && (
                <RoundButton onClick={addAbility}>
                  <i className="ti-plus" />
                </RoundButton>
              )}
            </Col>
          </div>
        </div>
        {props.abilities.length > 0 && (
          <SimulatedContentMapAbilities
            isCoordinator={props.isCoordinator}
            abilities={props.abilitiesFiltered}
            onEdit={handleEditAbility}
            onDelete={handleRequestRemoveAbility}
            onApprove={handleApproveAbility}
            onReprove={handleReproveAbility}
            onVisualize={handleVisualizeAbility}
          />
        )}
      </Row>
      <SimulatedAbilityModal
        idEvaluation={props.idEvaluation}
        type={abilityModalType}
        ability={ability}
        show={modals.ability}
        onClose={handleCloseAbilityModal}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onReprove={handleReprove}
        onApprove={handleApprove}
        idAlternativeMatrix={props.evaluation.evaluation.idAlternativeMatrix}
      />
      <SimulatedAbilityConfirmationModal
        show={modals.abilityConfirmation}
        type={abilityModalType}
        onClose={() => setModals({ abilityConfirmation: false })}
        onContinue={() => setModals({ abilityConfirmation: false })}
      />
      <SimulatedRemoveModal
        isOpen={modals.remove}
        onClose={() => setModals({ remove: false })}
        onContinue={removeAbility}
      />
      <SimulatedRemoveSuccessfullyModal
        isOpen={modals.removeSuccessfully}
        ability={ability}
        onClose={() => setModals({ removeSuccessfully: false })}
        onContinue={() => setModals({ removeSuccessfully: false })}
      />
      <SimulatedRemoveProblemModal
        isOpen={modals.removeProblem}
        feedback={errorFeedback}
        onClose={() => setModals({ removeProblem: false })}
        onContinue={() => setModals({ removeProblem: false })}
      />
      <SimulatedApproveModal
        show={modals.approve}
        status={props.approveAllStatus}
        onClose={handleCloseApproveAllAbilites}
        onContinue={handleApproveAllAbilities}
      />
    </React.Fragment>
  );
};

SimulatedContentMapContainer.propTypes = {};

function mapStateToProps({ simulatedContentMap }, { match }) {
  return {
    ...simulatedContentMap,
    ...match.params,
  };
}

export default compose(
  injectIntl,
  withRouter,
  connect(
    mapStateToProps,
    promisify({ ...actions, removeContentmapAbilities }, { exclude: ['applyFilter'] }),
  ),
)(SimulatedContentMapContainer);
